import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  ArrowLeft, 
  Building2, 
  User, 
  Plus, 
  Trash2, 
  Loader2,
  FileText,
  Clock,
  ChevronRight,
  Video,
  Settings,
  Calendar,
  Mail,
  Phone,
  Users,
  Shield,
  Briefcase,
  Construction,
  Search,
  Cloud,
  Check,
  Download,
  Pencil,
  X,
  LogIn,
  Copy,
  ExternalLink,
  Image as ImageIcon,
  RefreshCw,
  BarChart3,
  Presentation as PresentationIcon,
  Play,
  FileVideo,
  Sparkles,
  GraduationCap,
  Map,
  BrainCircuit,
  Clapperboard,
  Palette,
  Link2
} from 'lucide-react';
import WmsLaunchAdmin from './WmsLaunchAdmin';
import { toast } from 'sonner';
import { useAuth } from './AuthContext';
import './AdminPanel.css';
import { AppHeader } from './AppHeader';
import VideoAutomationModal from './VideoAutomationModal';
import StatsHub from './StatsHub';
import Presentations from './Presentations';
import Derek from './Derek';
import DerekKnowledge from './DerekKnowledge';
import AdminEnquiries from './AdminEnquiries';
import AdminRoadmap from './AdminRoadmap';
import RolesAccess, { UserAccessModal } from './RolesAccess';
import TemplateEditor from './TemplateEditor';
import CaptionStyleEditor from './CaptionStyleEditor';
import BrandPersonalityEditor from './BrandPersonalityEditor';
import EmploymentIntelligence from './EmploymentIntelligence';
import TemplateStyler from './TemplateStyler';
import BrandStylePanel from './BrandStylePanel';

const API = process.env.REACT_APP_BACKEND_URL;

// Derive a static JPG thumbnail from a Cloudinary video URL (fallback to <video> poster)
function deriveVideoPoster(videoUrl, explicitThumb) {
  if (explicitThumb) return explicitThumb;
  if (!videoUrl) return null;
  // Strategy 1: Cloudinary /video/... URL with f_mp4 transformation — swap to f_jpg
  if (videoUrl.includes('res.cloudinary.com') && videoUrl.includes('f_mp4')) {
    return videoUrl.replace('f_mp4', 'f_jpg');
  }
  // Strategy 2: Cloudinary /video/upload/ — inject so_0.5,f_jpg after /upload/
  if (videoUrl.includes('res.cloudinary.com/') && videoUrl.includes('/video/upload/')) {
    return videoUrl.replace('/video/upload/', '/video/upload/so_0.5,f_jpg,w_800/').replace(/\.[a-z0-9]+$/i, '.jpg');
  }
  // Strategy 3: Cloudinary /video/fetch/ without f_mp4 — prepend f_jpg,so_0.5 transformation
  if (videoUrl.includes('res.cloudinary.com/') && videoUrl.includes('/video/fetch/')) {
    return videoUrl.replace('/video/fetch/', '/video/fetch/so_0.5,f_jpg,w_800/');
  }
  return null;
}

function VideoThumbnail({ src, poster, testId }) {
  const [playing, setPlaying] = useState(false);
  const [imgFailed, setImgFailed] = useState(false);

  if (playing) {
    return (
      <video
        src={src}
        controls
        autoPlay
        preload="metadata"
        className="video-lib-video"
        data-testid={`${testId}-video`}
      />
    );
  }

  return (
    <button
      type="button"
      className="video-lib-thumb-btn"
      onClick={() => setPlaying(true)}
      data-testid={`${testId}-play`}
      aria-label="Play video"
    >
      {poster && !imgFailed ? (
        <img
          src={poster}
          alt=""
          className="video-lib-thumb-img"
          onError={() => setImgFailed(true)}
          loading="lazy"
        />
      ) : (
        <div className="video-lib-thumb-fallback">
          <FileVideo size={40} strokeWidth={1.25} />
        </div>
      )}
      <div className="video-lib-thumb-overlay">
        <div className="video-lib-play-circle">
          <Play size={22} fill="currentColor" />
        </div>
      </div>
    </button>
  );
}

function VideoLibrary() {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/api/video/submissions`);
        setSubmissions(res.data.submissions || []);
      } catch (e) {
        toast.error("Failed to load video submissions");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const copyLink = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied to clipboard");
    } catch { toast.error("Copy failed"); }
  };

  if (loading) return (
    <div className="admin-section" style={{ textAlign: 'center', padding: '3rem' }}>
      <Loader2 size={24} className="animate-spin" style={{ margin: '0 auto' }} />
    </div>
  );

  return (
    <div className="admin-section" data-testid="video-library-section">
      <div className="section-header">
        <h2>Video Library</h2>
        <span style={{ fontSize: '0.8rem', opacity: 0.6 }}>{submissions.length} video{submissions.length !== 1 ? 's' : ''}</span>
      </div>
      {submissions.length === 0 ? (
        <p style={{ opacity: 0.5, padding: '2rem', textAlign: 'center' }}>No submitted videos yet. Videos appear here after approval in the Social Video tool.</p>
      ) : (
        <div className="video-library-grid">
          {submissions.map((sub, i) => (
            <div key={sub.id || i} className="video-lib-card" data-testid={`video-submission-${i}`}>
              <div className="video-lib-preview">
                <VideoThumbnail
                  src={sub.video_url}
                  poster={deriveVideoPoster(sub.video_url, sub.thumbnail_url)}
                  testId={`video-submission-${i}`}
                />
              </div>
              <div className="video-lib-meta">
                <div className="video-lib-row">
                  <span className="video-lib-label">Format</span>
                  <span>{sub.format_label}</span>
                </div>
                <div className="video-lib-row">
                  <span className="video-lib-label">By</span>
                  <span>{sub.submitted_by_name || sub.submitted_by}</span>
                </div>
                {sub.wms_job_id && (
                  <div className="video-lib-row">
                    <span className="video-lib-label">Job</span>
                    <span>{sub.wms_job_id}</span>
                  </div>
                )}
                <div className="video-lib-row">
                  <span className="video-lib-label">Date</span>
                  <span>{new Date(sub.submitted_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                </div>
              </div>
              <div className="video-lib-actions">
                <button onClick={() => copyLink(sub.video_url)} className="video-lib-btn" data-testid={`copy-link-${i}`}>
                  <Copy size={14} /> Copy Link
                </button>
                <a href={sub.video_url} target="_blank" rel="noopener noreferrer" className="video-lib-btn" data-testid={`open-link-${i}`}>
                  <ExternalLink size={14} /> Open
                </a>
                <a href={sub.video_url} download className="video-lib-btn" target="_blank" rel="noopener noreferrer" data-testid={`download-link-${i}`}>
                  <Download size={14} /> Download
                </a>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AdminPanel({ onBack }) {
  const { user, impersonate } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const isClientManager = user?.role === 'client_manager';
  const isBranchAdmin = user?.role === 'branch_admin';
  
  // For super admin: clients/branches view
  // For client manager: their client's branches
  const [activeTab, setActiveTab] = useState(isSuperAdmin ? 'clients' : (isClientManager ? 'branches' : 'avatars'));
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  
  const [branches, setBranches] = useState([]);
  const [clients, setClients] = useState([]);
  const [agents, setAgents] = useState([]);
  const [avatars, setAvatars] = useState([]);
  const [editors, setEditors] = useState([]);
  const [myBranch, setMyBranch] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Users & Roles tab state
  const [roleSummary, setRoleSummary] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [usersSearch, setUsersSearch] = useState('');
  const [usersLoading, setUsersLoading] = useState(false);
  const [roleFilter, setRoleFilter] = useState('');
  const [branchFilter, setBranchFilter] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [usersView, setUsersView] = useState('users');
  const [accessUser, setAccessUser] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [createUserForm, setCreateUserForm] = useState({ name: '', email: '', password: '', role: 'estate_agent', branch_id: '', client_id: '', phone: '', branch_ids: [] });

  // Dashboard search
  const [dashSearch, setDashSearch] = useState('');
  const [dashSearchOpen, setDashSearchOpen] = useState(false);
  const [showBranchTemplates, setShowBranchTemplates] = useState(false);
  const [showCaptionStyle, setShowCaptionStyle] = useState(false);
  const [showBrandPersonality, setShowBrandPersonality] = useState(false);
  const [showBrandStyle, setShowBrandStyle] = useState(false);
  const [showClientBrandStyle, setShowClientBrandStyle] = useState(false);
  const [showVideoAutomation, setShowVideoAutomation] = useState(false);

  // WMS Import state
  const [wmsClientId, setWmsClientId] = useState('');
  const [wmsPreview, setWmsPreview] = useState(null);
  const [wmsLoading, setWmsLoading] = useState(false);
  const [wmsImporting, setWmsImporting] = useState(false);
  const [wmsResult, setWmsResult] = useState(null);
  const [wmsSelectedBranches, setWmsSelectedBranches] = useState([]);
  
  // Selected avatar for viewing scripts
  const [selectedAvatar, setSelectedAvatar] = useState(null);
  const [avatarScripts, setAvatarScripts] = useState([]);
  const [loadingScripts, setLoadingScripts] = useState(false);
  
  // Form states
  const [showBranchForm, setShowBranchForm] = useState(false);
  const [showAvatarForm, setShowAvatarForm] = useState(false);
  const [showEditorForm, setShowEditorForm] = useState(false);
  const [showBranchSettings, setShowBranchSettings] = useState(false);
  const [showAvatarSettings, setShowAvatarSettings] = useState(null);
  const [showManagerEdit, setShowManagerEdit] = useState(false);
  const [managerForm, setManagerForm] = useState({ name: '', email: '' });
  const [showClientForm, setShowClientForm] = useState(false);
  const [clientForm, setClientForm] = useState({ name: '', wms_client_id: '' });
  const [editingClient, setEditingClient] = useState(null);
  const [editClientForm, setEditClientForm] = useState({ name: '', wms_client_id: '' });
  const [clientBranchSearch, setClientBranchSearch] = useState('');
  const [togglingBranchId, setTogglingBranchId] = useState(null);
  const [managerSearchQuery, setManagerSearchQuery] = useState('');
  const [managerSearchResults, setManagerSearchResults] = useState([]);
  
  const [branchForm, setBranchForm] = useState({ name: '', email: '', password: '', adminName: '', address: '', phone: '', agencyStyle: 'Friendly & Conversational' });
  const [avatarForm, setAvatarForm] = useState({ agentName: '', avatarId: '', agentPersonality: 'Easy-going & Confident' });
  const [editorForm, setEditorForm] = useState({ name: '', email: '', password: '' });
  
  // Style and personality options with descriptions
  const agencyStyleOptions = [
    { 
      value: 'Friendly & Conversational', 
      description: 'Casual, approachable tone. Uses "you" and "your home". Best for first-time buyers and family homes.'
    },
    { 
      value: 'Premium & Polished', 
      description: 'Sophisticated, luxury feel. Emphasises quality and exclusivity. Ideal for high-end properties.'
    },
    { 
      value: 'Straight-Talking & Confident', 
      description: 'Direct and no-nonsense. Facts-focused with clear benefits. Great for investors and relocators.'
    },
    { 
      value: 'Warm & Reassuring', 
      description: 'Comforting and supportive tone. Highlights security and community. Perfect for downsizers.'
    },
    { 
      value: 'Clean & Clear', 
      description: 'No fluff, no embellishment. Just the facts - what each room is and what it has. Pure property tour.'
    }
  ];
  
  const personalityOptions = [
    { 
      value: 'Easy-going & Confident', 
      description: 'Relaxed delivery with natural pauses. Sounds like a friendly chat. The default all-rounder.'
    },
    { 
      value: 'Energetic & Upbeat', 
      description: 'Enthusiastic and fast-paced. Creates excitement and urgency. Great for new builds and modern homes.'
    },
    { 
      value: 'Calm & Professional', 
      description: 'Measured and articulate. Authoritative without being pushy. Suits executive and period properties.'
    },
    { 
      value: 'Mature & Reassuring', 
      description: 'Experienced and trustworthy tone. Patient pacing. Ideal for retirement properties and estates.'
    }
  ];

  // Helper to check if an avatar is assigned to any agent
  const isAvatarAssigned = (avatarId) => {
    return agents.some(agent => agent.avatar_id === avatarId);
  };

  const ROLE_ICONS = {
    super_admin: Shield,
    client_manager: Briefcase,
    branch_manager: Building2,
    estate_agent: User,
    editor: FileText,
  };

  // Get agent assigned to avatar
  const getAgentForAvatar = (avatarId) => {
    return agents.find(agent => agent.avatar_id === avatarId);
  };

  // Get avatars for current view (filtered by branch for super admin)
  const getFilteredAvatars = () => {
    if (isBranchAdmin) {
      return avatars; // Already filtered by backend
    }
    if (selectedBranch) {
      return avatars.filter(a => a.branch_id === selectedBranch.id);
    }
    return avatars;
  };

  // Get agents for current branch view
  const getFilteredAgents = () => {
    if (isBranchAdmin) {
      return agents;
    }
    if (selectedBranch) {
      return agents.filter(a => a.branch_id === selectedBranch.id);
    }
    return agents;
  };

  const loadData = async () => {
    setLoading(true);
    try {
      if (isSuperAdmin) {
        const [branchesRes, agentsRes, avatarsRes, editorsRes, clientsRes] = await Promise.all([
          axios.get(`${API}/api/admin/branches`),
          axios.get(`${API}/api/admin/agents`),
          axios.get(`${API}/api/admin/avatars`),
          axios.get(`${API}/api/admin/editors`),
          axios.get(`${API}/api/admin/clients`)
        ]);
        setBranches(branchesRes.data);
        setAgents(agentsRes.data);
        setAvatars(avatarsRes.data);
        setEditors(editorsRes.data);
        setClients(clientsRes.data.clients || []);
      } else if (isClientManager) {
        const [branchesRes, clientsRes] = await Promise.all([
          axios.get(`${API}/api/admin/branches`),
          axios.get(`${API}/api/admin/clients`)
        ]);
        setBranches(branchesRes.data);
        setClients(clientsRes.data.clients || []);
      } else if (isBranchAdmin) {
        const [branchRes, agentsRes, avatarsRes] = await Promise.all([
          axios.get(`${API}/api/admin/my-branch`),
          axios.get(`${API}/api/admin/agents`),
          axios.get(`${API}/api/admin/avatars`)
        ]);
        setMyBranch(branchRes.data);
        setAgents(agentsRes.data);
        setAvatars(avatarsRes.data);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    if (isSuperAdmin) {
      loadUsersData();
    }
  }, [user?.role]);

  // Load users & role summary when Users tab is active
  const loadUsersData = async (search = '', role = '', branch = '') => {
    setUsersLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (role) params.append('role', role);
      if (branch) params.append('branch_id', branch);
      const qStr = params.toString() ? `?${params.toString()}` : '';
      
      const [summaryRes, usersRes] = await Promise.all([
        axios.get(`${API}/api/admin/role-summary`),
        axios.get(`${API}/api/admin/users${qStr}`)
      ]);
      setRoleSummary(summaryRes.data.roles);
      setAllUsers(usersRes.data.users);
    } catch (error) {
      console.error('Failed to load users:', error);
    } finally {
      setUsersLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'users' && isSuperAdmin) {
      loadUsersData(usersSearch, roleFilter, branchFilter);
    }
  }, [activeTab]);

  const handleUsersSearch = (value) => {
    setUsersSearch(value);
    loadUsersData(value, roleFilter, branchFilter);
  };

  const handleRoleFilter = (value) => {
    setRoleFilter(value);
    loadUsersData(usersSearch, value, branchFilter);
  };

  const handleBranchFilterChange = (value) => {
    setBranchFilter(value);
    loadUsersData(usersSearch, roleFilter, value);
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setEditForm({
      name: user.name,
      email: user.email,
      role: user.role,
      branch_id: user.branch_id || '',
      client_id: user.client_id || '',
      phone: user.phone || '',
      branch_ids: user.branch_ids || [],
      demo_user: !!user.demo_user,
      asset_hub_access: user.asset_hub_explicit === null || user.asset_hub_explicit === undefined ? !!user.asset_hub_access : !!user.asset_hub_explicit
    });
  };

  const handleSaveUser = async () => {
    if (!editingUser) return;
    try {
      const payload = { ...editForm };
      // Only pin the Asset Hub flag when the admin actually changed it — otherwise keep the role default
      if ((editingUser.asset_hub_explicit === null || editingUser.asset_hub_explicit === undefined) && payload.asset_hub_access === !!editingUser.asset_hub_access) delete payload.asset_hub_access;
      await axios.put(`${API}/api/admin/users/${editingUser.id}`, payload);
      toast.success('User updated');
      setEditingUser(null);
      loadUsersData(usersSearch, roleFilter, branchFilter);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Delete user "${userName}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/api/admin/users/${userId}`);
      toast.success('User deleted');
      loadUsersData(usersSearch, roleFilter, branchFilter);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleCreateUser = async () => {
    if (!createUserForm.name || !createUserForm.email || !createUserForm.password) {
      toast.error('Name, email, and password are required');
      return;
    }
    try {
      await axios.post(`${API}/api/admin/users`, createUserForm);
      toast.success('User created');
      setShowCreateUser(false);
      setCreateUserForm({ name: '', email: '', password: '', role: 'estate_agent', branch_id: '', client_id: '', phone: '', branch_ids: [] });
      loadUsersData(usersSearch, roleFilter, branchFilter);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  // Group users by role
  const usersByRole = allUsers.reduce((acc, u) => {
    const r = u.role || 'estate_agent';
    if (!acc[r]) acc[r] = [];
    acc[r].push(u);
    return acc;
  }, {});

  // WMS Functions
  const handleWmsPreview = async () => {
    if (!wmsClientId.trim()) return;
    setWmsLoading(true);
    setWmsPreview(null);
    setWmsResult(null);
    setWmsSelectedBranches([]);
    try {
      const response = await axios.get(`${API}/api/wms/client/${wmsClientId.trim()}`);
      setWmsPreview(response.data);
      // Auto-select non-imported branches
      const selectable = response.data.branches.filter(b => !b.already_imported).map(b => b.wms_id);
      setWmsSelectedBranches(selectable);
    } catch (error) {
      const detail = error.response?.data?.detail || "Client not found";
      toast.error(detail);
    } finally {
      setWmsLoading(false);
    }
  };

  const toggleWmsBranch = (wmsId) => {
    setWmsSelectedBranches(prev =>
      prev.includes(wmsId) ? prev.filter(id => id !== wmsId) : [...prev, wmsId]
    );
  };

  const handleWmsImport = async () => {
    if (wmsSelectedBranches.length === 0) {
      toast.error('Select at least one branch to import');
      return;
    }
    setWmsImporting(true);
    try {
      const response = await axios.post(`${API}/api/wms/import-client`, {
        client_id: parseInt(wmsClientId.trim()),
        selected_branch_ids: wmsSelectedBranches
      });
      setWmsResult(response.data);
      toast.success(`Imported ${response.data.imported} branches from WMS`);
      loadData();
    } catch (error) {
      const detail = error.response?.data?.detail || "Import failed";
      toast.error(detail);
    } finally {
      setWmsImporting(false);
    }
  };


  // ── Client Management ──
  const handleCreateClient = async (e) => {
    e.preventDefault();
    if (!clientForm.wms_client_id) return;
    try {
      toast.info('Importing client & branches from WMS...');
      const res = await axios.post(`${API}/api/admin/clients`, {
        name: `Client ${clientForm.wms_client_id}`,
        wms_client_id: parseInt(clientForm.wms_client_id)
      });
      const branchCount = res.data.branch_count || 0;
      toast.success(`"${res.data.name}" imported with ${branchCount} branches`);
      setShowClientForm(false);
      setClientForm({ name: '', wms_client_id: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import client');
    }
  };

  const handleUpdateClient = async () => {
    if (!editingClient) return;
    try {
      await axios.put(`${API}/api/admin/clients/${editingClient.id}`, {
        name: editClientForm.name,
        wms_client_id: editClientForm.wms_client_id ? parseInt(editClientForm.wms_client_id) : null
      });
      toast.success('Client updated');
      setEditingClient(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update client');
    }
  };

  const handleDeleteClient = async (clientId, clientName) => {
    if (!window.confirm(`Delete client "${clientName}"? Branches will be unlinked.`)) return;
    try {
      await axios.delete(`${API}/api/admin/clients/${clientId}`);
      toast.success('Client deleted');
      if (selectedClient?.id === clientId) setSelectedClient(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete client');
    }
  };

  const handleSelectClient = (client) => {
    setSelectedClient(client);
    setActiveTab('branches');
    setClientBranchSearch('');
    window.scrollTo(0, 0);
  };

  const handleBackToClients = () => {
    setSelectedClient(null);
    setShowClientBrandStyle(false);
    setActiveTab('clients');
    setSelectedBranch(null);
    window.scrollTo(0, 0);
  };

  const handleToggleBranch = async (branchId, branchName, currentEnabled) => {
    setTogglingBranchId(branchId);
    try {
      const res = await axios.patch(`${API}/api/admin/branches/${branchId}/toggle`);
      // Update local state — keeps scroll position
      setBranches(prev => prev.map(b => b.id === branchId ? { 
        ...b, 
        enabled: res.data.enabled,
        agent_count: b.agent_count + (res.data.agents_imported || 0),
        warming_up: res.data.warming_up || false,
      } : b));
      if (res.data.enabled) {
        const newAgents = res.data.agents_imported || 0;
        const existingAgents = res.data.agents_existing || 0;
        const total = newAgents + existingAgents;
        const warmMsg = res.data.warming_up ? ' Data loading in background — ready in ~2 mins.' : '';
        toast.success(total > 0 
          ? `${branchName} enabled — ${newAgents} new agents imported${existingAgents > 0 ? `, ${existingAgents} already existed` : ''}.${warmMsg}`
          : `${branchName} enabled.${warmMsg}`
        );
        // Clear warming_up after 2 minutes
        if (res.data.warming_up) {
          setTimeout(() => {
            setBranches(prev => prev.map(b => b.id === branchId ? { ...b, warming_up: false } : b));
          }, 120000);
        }
      } else {
        toast.success(`${branchName} disabled`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to toggle branch');
    } finally {
      setTogglingBranchId(null);
    }
  };

  const handleAssignBranchToClient = async (branchId, clientId) => {
    try {
      await axios.post(`${API}/api/admin/clients/${clientId}/branches/${branchId}`);
      toast.success('Branch assigned to client');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign branch');
    }
  };


  const handleCreateBranch = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/api/admin/branches`, {
        email: branchForm.email,
        password: branchForm.password,
        name: branchForm.adminName,
        branch_name: branchForm.name,
        branch_address: branchForm.address,
        branch_phone: branchForm.phone,
        agency_style: branchForm.agencyStyle,
        client_id: selectedClient?.id || null
      });
      toast.success('Branch created successfully');
      setShowBranchForm(false);
      setBranchForm({ name: '', email: '', password: '', adminName: '', address: '', phone: '', agencyStyle: 'Friendly & Conversational' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create branch');
    }
  };

  const handleCreateEditor = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/api/admin/editors`, {
        email: editorForm.email,
        password: editorForm.password,
        name: editorForm.name
      });
      toast.success('Editor created successfully');
      setShowEditorForm(false);
      setEditorForm({ name: '', email: '', password: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create editor');
    }
  };

  const handleDeleteEditor = async (editorId) => {
    if (!window.confirm('Are you sure you want to remove this editor?')) return;
    try {
      await axios.delete(`${API}/api/admin/editors/${editorId}`);
      toast.success('Editor removed');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete editor');
    }
  };


  const handleUpdateBranchStyle = async (branchId, newStyle) => {
    try {
      await axios.patch(`${API}/api/admin/branches/${branchId}`, {
        agency_style: newStyle
      });
      toast.success('Agency style updated');
      loadData();
      // Update myBranch if it's the current branch
      if (myBranch?.id === branchId) {
        setMyBranch(prev => ({ ...prev, agency_style: newStyle }));
      }
    } catch (error) {
      toast.error('Failed to update agency style');
    }
  };

  const handleDeleteBranch = async (branchId) => {
    if (!window.confirm('Delete this branch? This will remove all avatars and their data.')) return;
    try {
      await axios.delete(`${API}/api/admin/branches/${branchId}`);
      toast.success('Branch deleted');
      setSelectedBranch(null);
      loadData();
    } catch (error) {
      toast.error('Failed to delete branch');
    }
  };

  const handleUpdateBranchManager = async (e) => {
    e.preventDefault();
    if (!selectedBranch) return;
    
    try {
      await axios.patch(`${API}/api/admin/branches/${selectedBranch.id}/manager`, {
        name: managerForm.name,
        email: managerForm.email
      });
      toast.success('Branch manager updated');
      setShowManagerEdit(false);
      setManagerSearchQuery('');
      setManagerSearchResults([]);
      // Refresh data and update selected branch
      const branchesRes = await axios.get(`${API}/api/admin/branches`);
      setBranches(branchesRes.data);
      const updatedBranch = branchesRes.data.find(b => b.id === selectedBranch.id);
      if (updatedBranch) {
        setSelectedBranch(updatedBranch);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update branch manager');
    }
  };

  const openManagerEdit = () => {
    if (selectedBranch) {
      setManagerForm({
        name: selectedBranch.admin_name || '',
        email: selectedBranch.admin_email || ''
      });
      setShowManagerEdit(true);
      setManagerSearchQuery('');
      setManagerSearchResults([]);
    }
  };

  const handleManagerSearch = (query) => {
    setManagerSearchQuery(query);
    if (query.length < 2) {
      setManagerSearchResults([]);
      return;
    }
    const q = query.toLowerCase();
    const results = allUsers.filter(u =>
      (u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q))
    ).slice(0, 8);
    setManagerSearchResults(results);
  };

  const handleAssignManager = (userObj) => {
    setManagerForm({ name: userObj.name, email: userObj.email });
    setManagerSearchQuery('');
    setManagerSearchResults([]);
  };

  const handleSelectBranch = (branch) => {
    setSelectedBranch(branch);
    setActiveTab('avatars');
    setSelectedAvatar(null);
    window.scrollTo(0, 0);
  };

  const handleBackToBranches = () => {
    setSelectedBranch(null);
    setActiveTab('branches');
    setSelectedAvatar(null);
    setShowBranchTemplates(false);
    setShowCaptionStyle(false);
    setShowBrandStyle(false);
    window.scrollTo(0, 0);
  };

  const handleCreateAvatar = async (e) => {
    e.preventDefault();
    const branchId = selectedBranch?.id || myBranch?.id;
    if (!branchId) {
      toast.error('No branch selected');
      return;
    }
    try {
      await axios.post(`${API}/api/admin/avatars?branch_id=${branchId}`, {
        agent_name: avatarForm.agentName,
        avatar_id: avatarForm.avatarId,
        agent_personality: avatarForm.agentPersonality
      });
      toast.success('Avatar created successfully');
      setShowAvatarForm(false);
      setAvatarForm({ agentName: '', avatarId: '', agentPersonality: 'Easy-going & Confident' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create avatar');
    }
  };

  const handleDeleteAvatar = async (avatarId) => {
    if (!window.confirm('Delete this avatar? This will also remove any assigned agent.')) return;
    try {
      await axios.delete(`${API}/api/admin/avatars/${avatarId}`);
      toast.success('Avatar deleted');
      setSelectedAvatar(null);
      loadData();
    } catch (error) {
      toast.error('Failed to delete avatar');
    }
  };

  const handleUpdateAvatarPersonality = async (avatarId, newPersonality) => {
    try {
      await axios.patch(`${API}/api/admin/avatars/${avatarId}`, {
        agent_personality: newPersonality
      });
      toast.success('Agent personality updated');
      loadData();
      setShowAvatarSettings(null);
    } catch (error) {
      toast.error('Failed to update personality');
    }
  };

  const handleDeleteAgent = async (agentId) => {
    if (!window.confirm('Remove this agent? The avatar slot will become available.')) return;
    try {
      await axios.delete(`${API}/api/admin/agents/${agentId}`);
      toast.success('Agent removed - avatar slot now available');
      setSelectedAvatar(null);
      loadData();
    } catch (error) {
      toast.error('Failed to remove agent');
    }
  };

  const handleSelectAvatar = async (avatar) => {
    const agent = getAgentForAvatar(avatar.id);
    setSelectedAvatar({ ...avatar, agent });
    
    if (agent) {
      setLoadingScripts(true);
      try {
        const response = await axios.get(`${API}/api/admin/agents/${agent.id}/scripts`);
        setAvatarScripts(response.data);
      } catch (error) {
        toast.error('Failed to load scripts');
      } finally {
        setLoadingScripts(false);
      }
    } else {
      setAvatarScripts([]);
    }
  };

  const handleLoginAsUser = async (targetUserId) => {
    try {
      const res = await axios.post(`${API}/api/admin/impersonate/${targetUserId}`);
      impersonate(res.data.access_token, res.data.user);
      toast.success(`Now viewing as ${res.data.user.name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to impersonate user');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <Loader2 className="animate-spin" size={32} />
        <span>Loading admin panel...</span>
      </div>
    );
  }

  const currentBranchName = selectedBranch?.name || myBranch?.name || '';
  const filteredAvatars = getFilteredAvatars();

  // Dashboard search - filter data based on active tab
  const searchLower = dashSearch.toLowerCase();
  const clientFilteredBranches = selectedClient 
    ? branches.filter(b => b.client_id === selectedClient.id)
    : branches;
  const clientSearchLower = clientBranchSearch.toLowerCase();
  const filteredBranches = selectedClient
    ? (clientSearchLower 
        ? clientFilteredBranches.filter(b => b.name?.toLowerCase().includes(clientSearchLower))
        : clientFilteredBranches)
    : (searchLower
        ? clientFilteredBranches.filter(b => b.name?.toLowerCase().includes(searchLower) || b.admin_name?.toLowerCase().includes(searchLower))
        : clientFilteredBranches);
  const filteredEditors = searchLower
    ? editors.filter(e => e.name?.toLowerCase().includes(searchLower) || e.email?.toLowerCase().includes(searchLower))
    : editors;

  const handleDashSearch = (value) => {
    setDashSearch(value);
    if (activeTab === 'users') {
      setUsersSearch(value);
      loadUsersData(value, roleFilter, branchFilter);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setDashSearch(tab === 'users' ? usersSearch : '');
  };

  const searchPlaceholders = {
    branches: 'Search branches...',
    editors: 'Search editors...',
    users: 'Search users by name or email...',
    wms: 'Enter WMS Client ID below...',
  };

  // Employment Intelligence lives inside the Admin Panel (full-screen takeover)
  if (activeTab === 'employment_intelligence' && isSuperAdmin) {
    return <EmploymentIntelligence onBack={() => setActiveTab('clients')} />;
  }

  return (
    <div className="admin-panel">
      <AppHeader onBack={onBack} />
      <div className="admin-header">
        <h1 className="admin-title">
          <Settings size={24} />
          Admin Panel
        </h1>
        <span className="admin-role">
          {isSuperAdmin ? 'Super Admin' : isClientManager ? `Client Manager${user?.client_name ? ` - ${user.client_name}` : ''}` : `Branch Admin - ${myBranch?.name}`}
        </span>
      </div>

      {/* Tabs */}
      {(isSuperAdmin || isClientManager) && !selectedBranch && !selectedClient && (
        <div className="admin-tabs">
          {isSuperAdmin && (
            <button 
              className={`admin-tab ${activeTab === 'clients' ? 'active' : ''}`}
              onClick={() => handleTabChange('clients')}
              data-testid="clients-tab"
            >
              <Briefcase size={18} />
              Clients
            </button>
          )}
          <button 
            className={`admin-tab ${activeTab === 'branches' ? 'active' : ''}`}
            onClick={() => handleTabChange('branches')}
          >
            <Building2 size={18} />
            Branches
          </button>
          {isSuperAdmin && (
            <>
              <button 
                className={`admin-tab ${activeTab === 'editors' ? 'active' : ''}`}
                onClick={() => handleTabChange('editors')}
              >
                <FileText size={18} />
                Editors
              </button>
              <button 
                className={`admin-tab ${activeTab === 'users' ? 'active' : ''}`}
                onClick={() => handleTabChange('users')}
                data-testid="users-roles-tab"
              >
                <Users size={18} />
                Users &amp; Roles
              </button>
              <button 
                className={`admin-tab ${activeTab === 'videos' ? 'active' : ''}`}
                onClick={() => handleTabChange('videos')}
                data-testid="video-library-tab"
              >
                <Video size={18} />
                Video Library
              </button>
              <button
                className={`admin-tab ${activeTab === 'brand_styler' ? 'active' : ''}`}
                onClick={() => handleTabChange('brand_styler')}
                data-testid="brand-styler-tab"
              >
                <Palette size={18} />
                Brand Styler
              </button>
              <button
                className={`admin-tab ${activeTab === 'stats_hub' ? 'active' : ''}`}
                onClick={() => handleTabChange('stats_hub')}
                data-testid="stats-hub-tab"
              >
                <BarChart3 size={18} />
                Stats Hub
              </button>
              <button
                className={`admin-tab ${activeTab === 'presentations' ? 'active' : ''}`}
                onClick={() => handleTabChange('presentations')}
                data-testid="presentations-tab"
              >
                <PresentationIcon size={18} />
                Presentation
              </button>
              <button
                className={`admin-tab ${activeTab === 'derek_training' ? 'active' : ''}`}
                onClick={() => handleTabChange('derek_training')}
                data-testid="derek-training-tab"
              >
                <GraduationCap size={18} />
                Derek Training
              </button>
              <button
                className={`admin-tab ${activeTab === 'derek' ? 'active' : ''}`}
                onClick={() => handleTabChange('derek')}
                data-testid="derek-tab"
              >
                <Sparkles size={18} />
                Derek
              </button>
              <button
                className={`admin-tab ${activeTab === 'enquiries' ? 'active' : ''}`}
                onClick={() => handleTabChange('enquiries')}
                data-testid="enquiries-tab"
              >
                <Mail size={18} />
                Enquiries
              </button>
              <button
                className={`admin-tab ${activeTab === 'roadmap' ? 'active' : ''}`}
                onClick={() => handleTabChange('roadmap')}
                data-testid="roadmap-tab"
              >
                <Map size={18} />
                Roadmap
              </button>
              <button
                className={`admin-tab ${activeTab === 'wms' ? 'active' : ''}`}
                onClick={() => handleTabChange('wms')}
                data-testid="wms-tab"
              >
                <Link2 size={18} />
                WMS Launch
              </button>
              <button
                className={`admin-tab ${activeTab === 'employment_intelligence' ? 'active' : ''}`}
                onClick={() => handleTabChange('employment_intelligence')}
                data-testid="employment-intelligence-tab"
              >
                <BrainCircuit size={18} />
                EI Hub
              </button>
            </>
          )}
        </div>
      )}

      {/* Client breadcrumb when viewing a client's branches */}
      {selectedClient && !selectedBranch && (
        <div className="branch-view-header">
          <div className="branch-view-top">
            <button onClick={handleBackToClients} className="back-to-branches">
              <ArrowLeft size={16} />
              All Clients
            </button>
          </div>
          <div className="branch-view-info">
            <Briefcase size={20} />
            <span className="branch-view-name">{selectedClient.name}</span>
            <span className="branch-count-badge">{clientFilteredBranches.length} branches</span>
            {isSuperAdmin && (
              <button
                className={`sync-branches-btn client-brand-style-btn ${showClientBrandStyle ? 'active' : ''}`}
                onClick={() => setShowClientBrandStyle(prev => !prev)}
                data-testid="client-brand-style-btn"
                title="The brand profile every branch under this brand renders reels with"
              >
                <Palette size={13} />
                Brand Styler
              </button>
            )}
            {selectedClient.wms_client_id && (
              <button
                className="sync-branches-btn"
                onClick={async () => {
                  try {
                    toast.info('Syncing branches from WMS...');
                    const res = await axios.post(`${API}/api/admin/clients/${selectedClient.id}/sync-branches`);
                    const { imported = 0, relinked = 0, total_wms = 0 } = res.data;
                    if (imported > 0 || relinked > 0) {
                      const parts = [];
                      if (imported > 0) parts.push(`${imported} new`);
                      if (relinked > 0) parts.push(`${relinked} relinked`);
                      toast.success(`Synced: ${parts.join(', ')}`);
                    } else {
                      toast.info(`All ${total_wms} WMS branches already synced to this client`);
                    }
                    loadData();
                  } catch (err) {
                    toast.error(err.response?.data?.detail || 'Sync failed');
                  }
                }}
                data-testid="sync-branches-btn"
              >
                <RefreshCw size={13} />
                Sync WMS
              </button>
            )}
          </div>
          {showClientBrandStyle && isSuperAdmin && (
            <BrandStylePanel
              scope="client"
              target={selectedClient}
              onClose={() => setShowClientBrandStyle(false)}
              onOpenStyler={() => { setShowClientBrandStyle(false); handleBackToClients(); handleTabChange('brand_styler'); }}
            />
          )}
          {clientFilteredBranches.length > 5 && (
            <div className="client-branch-search" data-testid="client-branch-search">
              <Search size={16} />
              <input
                type="text"
                placeholder="Search branches..."
                value={clientBranchSearch}
                onChange={(e) => setClientBranchSearch(e.target.value)}
                data-testid="client-branch-search-input"
              />
              {clientBranchSearch && (
                <button className="clear-search" onClick={() => setClientBranchSearch('')}>
                  <X size={14} />
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Dashboard Search Bar */}
      {(isSuperAdmin || isClientManager) && !selectedBranch && !selectedClient && activeTab !== 'clients' && activeTab !== 'stats_hub' && activeTab !== 'presentations' && activeTab !== 'derek' && activeTab !== 'derek_training' && activeTab !== 'enquiries' && activeTab !== 'roadmap' && activeTab !== 'wms' && activeTab !== 'brand_styler' && (
        <div className={`dash-search-bar ${dashSearchOpen ? 'open' : ''}`} data-testid="dash-search-bar">
          <button
            className="dash-search-trigger"
            onClick={() => { setDashSearchOpen(o => !o); if (dashSearchOpen && dashSearch) handleDashSearch(''); }}
            data-testid="dash-search-trigger"
          >
            <Search size={18} />
          </button>
          <div className="dash-search-field">
            <input
              type="text"
              className="dash-search-input"
              placeholder={searchPlaceholders[activeTab] || 'Search...'}
              value={dashSearch}
              onChange={(e) => handleDashSearch(e.target.value)}
              data-testid="dash-search-input"
              autoFocus={dashSearchOpen}
            />
            {dashSearch && (
              <button className="dash-search-clear" onClick={() => handleDashSearch('')}>
                <X size={14} />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Branch Header when viewing a specific branch */}
      {isSuperAdmin && selectedBranch && (
        <div className="branch-view-header">
          <div className="branch-view-top">
            <button onClick={handleBackToBranches} className="back-to-branches">
              <ArrowLeft size={16} />
              All Branches
            </button>
            <div className="branch-view-actions">
              <button 
                onClick={() => { setShowBranchTemplates(prev => !prev); setShowCaptionStyle(false); setShowBrandStyle(false); }}
                className={`template-editor-btn ${showBranchTemplates ? 'active' : ''}`}
                data-testid="branch-template-editor-btn"
                title="Manage templates for this branch"
              >
                <ImageIcon size={16} />
                {showBranchTemplates ? 'Back to Branch' : 'Template Editor'}
              </button>
              <button
                onClick={() => { setShowCaptionStyle(prev => !prev); setShowBranchTemplates(false); setShowBrandPersonality(false); setShowBrandStyle(false); }}
                className={`template-editor-btn caption-style-btn ${showCaptionStyle ? 'active' : ''}`}
                data-testid="branch-caption-style-btn"
                title="Configure AI caption style for this branch"
              >
                <FileText size={16} />
                {showCaptionStyle ? 'Back to Branch' : 'Social Voice'}
              </button>
              <button
                onClick={() => { setShowBrandPersonality(prev => !prev); setShowBranchTemplates(false); setShowCaptionStyle(false); setShowBrandStyle(false); }}
                className={`template-editor-btn brand-personality-btn ${showBrandPersonality ? 'active' : ''}`}
                data-testid="branch-brand-personality-btn"
                title="Set the brand tone, rules, and Property Description preferences for this branch"
              >
                <Sparkles size={16} />
                {showBrandPersonality ? 'Back to Branch' : 'Brand Personality'}
              </button>
              <button
                onClick={() => { setShowBrandStyle(prev => !prev); setShowBranchTemplates(false); setShowCaptionStyle(false); setShowBrandPersonality(false); }}
                className={`template-editor-btn brand-style-btn ${showBrandStyle ? 'active' : ''}`}
                data-testid="branch-brand-style-btn"
                title="Which brand colours, fonts and logo this branch's reels render with"
              >
                <Palette size={16} />
                {showBrandStyle ? 'Back to Branch' : 'Brand Styler'}
              </button>
              <button
                onClick={() => setShowVideoAutomation(true)}
                className={`template-editor-btn video-automation-btn ${selectedBranch.synthesia?.api_key_enc || selectedBranch.synthesia_linked ? 'linked' : ''}`}
                data-testid="branch-video-automation-btn"
                title="Link this branch's Synthesia account and manage Social Pro script gating"
              >
                <Clapperboard size={16} />
                Video Automation
              </button>
              <button
                onClick={async () => {
                  const newVal = !selectedBranch.social_week_access;
                  try {
                    await axios.patch(`${API}/api/admin/branches/${selectedBranch.id}`, {
                      social_week_access: newVal,
                    });
                    setSelectedBranch(prev => prev ? { ...prev, social_week_access: newVal } : prev);
                    setBranches(prev => prev.map(b => b.id === selectedBranch.id ? { ...b, social_week_access: newVal } : b));
                    toast.success(newVal ? 'Social Week enabled for this branch' : 'Social Week disabled for this branch');
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || 'Failed to update access');
                  }
                }}
                className={`template-editor-btn social-week-access-btn ${selectedBranch.social_week_access ? 'sw-access-on' : 'sw-access-off'}`}
                data-testid="branch-social-week-access-btn"
                title={selectedBranch.social_week_access ? 'Social Week is enabled — click to disable' : 'Social Week is disabled — click to enable'}
              >
                <Calendar size={16} />
                Social Week: {selectedBranch.social_week_access ? 'On' : 'Off'}
              </button>
              <button 
                onClick={() => handleDeleteBranch(selectedBranch.id)} 
                className="delete-branch-btn"
                title="Delete this branch"
              >
                <Trash2 size={16} />
                Delete Branch
              </button>
            </div>
          </div>
          <div className="branch-view-info">
            <Building2 size={20} />
            <span className="branch-view-name">{selectedBranch.name}</span>
          </div>
        </div>
      )}

      <div className="admin-content">
        {/* CLIENT LOGO PRESETS — Super Admin Only */}
        {/* BRAND STYLER — the editors' Template Styler, super admin */}
        {activeTab === 'brand_styler' && isSuperAdmin && !selectedBranch && !selectedClient && (
          <TemplateStyler />
        )}

        {/* STATS HUB — Super Admin Only */}
        {activeTab === 'stats_hub' && isSuperAdmin && (
          <StatsHub token={localStorage.getItem('token')} />
        )}

        {/* PRESENTATIONS — Super Admin Only */}
        {activeTab === 'presentations' && isSuperAdmin && (
          <Presentations />
        )}

        {/* DEREK TRAINING — Super Admin Only */}
        {activeTab === 'derek_training' && isSuperAdmin && (
          <div className="derek-training-shell">
            <DerekKnowledge />
          </div>
        )}

        {/* DEREK — Super Admin Only */}
        {activeTab === 'derek' && isSuperAdmin && (
          <Derek />
        )}

        {/* ENQUIRIES — Super Admin Only */}
        {activeTab === 'enquiries' && isSuperAdmin && (
          <AdminEnquiries />
        )}

        {/* ROADMAP — Super Admin Only */}
        {activeTab === 'roadmap' && isSuperAdmin && (
          <AdminRoadmap />
        )}

        {/* WMS LAUNCH BUTTON — Super Admin Only */}
        {activeTab === 'wms' && isSuperAdmin && (
          <WmsLaunchAdmin />
        )}

        {/* CLIENTS LIST - Super Admin Only */}
        {activeTab === 'clients' && isSuperAdmin && !selectedBranch && !selectedClient && (
          <div className="admin-section" data-testid="clients-section">
            <div className="section-header">
              <h2>Clients (Agencies)</h2>
              <Button onClick={() => setShowClientForm(true)} className="add-btn" data-testid="add-client-btn">
                <Plus size={16} /> Add Client
              </Button>
            </div>

            {showClientForm && (
              <form onSubmit={handleCreateClient} className="admin-form" data-testid="create-client-form">
                <h3>Add Client from WMS</h3>
                <p style={{fontSize: '13px', opacity: 0.7, marginBottom: '12px'}}>Enter the WMS Client ID — the name and branches will be pulled in automatically.</p>
                <div className="form-grid">
                  <div className="form-group">
                    <Label>WMS Client ID *</Label>
                    <Input 
                      type="number"
                      value={clientForm.wms_client_id}
                      onChange={(e) => setClientForm({...clientForm, wms_client_id: e.target.value})}
                      required
                      placeholder="e.g., 615"
                      data-testid="client-wms-id-input"
                    />
                  </div>
                </div>
                <div className="form-actions">
                  <Button type="button" variant="outline" onClick={() => setShowClientForm(false)}>Cancel</Button>
                  <Button type="submit" data-testid="create-client-submit">Import Client</Button>
                </div>
              </form>
            )}

            <div className="items-list">
              {clients.length === 0 ? (
                <div className="empty-state">
                  <Briefcase size={48} />
                  <p>No clients yet</p>
                  <span>Add a client to start organising branches</span>
                </div>
              ) : (
                clients.map((client) => (
                  <div 
                    key={client.id} 
                    className="item-card clickable"
                    onClick={() => handleSelectClient(client)}
                    data-testid={`client-card-${client.id}`}
                  >
                    {editingClient?.id === client.id ? (
                      <div className="inline-edit-row" onClick={(e) => e.stopPropagation()}>
                        <input 
                          value={editClientForm.name} 
                          onChange={(e) => setEditClientForm(f => ({...f, name: e.target.value}))} 
                          className="inline-edit" 
                          placeholder="Client name"
                          data-testid="edit-client-name"
                        />
                        <input 
                          type="number"
                          value={editClientForm.wms_client_id} 
                          onChange={(e) => setEditClientForm(f => ({...f, wms_client_id: e.target.value}))} 
                          className="inline-edit" 
                          placeholder="WMS ID"
                          style={{width: 100}}
                        />
                        <button className="action-save" onClick={handleUpdateClient}><Check size={14} /></button>
                        <button className="action-cancel" onClick={() => setEditingClient(null)}><X size={14} /></button>
                      </div>
                    ) : (
                      <>
                        <div className="item-icon branch-icon">
                          <Briefcase size={20} />
                        </div>
                        <div className="item-info">
                          <span className="item-name">{client.name}</span>
                          <span className="item-meta">
                            {client.branch_count} branches ({client.enabled_branch_count} enabled)
                          </span>
                          {client.wms_client_id && (
                            <span className="item-stats">WMS ID: {client.wms_client_id}</span>
                          )}
                        </div>
                        <div className="item-actions" onClick={(e) => { e.stopPropagation(); e.preventDefault(); }}>
                          <button 
                            className="action-edit"
                            onClick={(e) => { e.stopPropagation(); setEditingClient(client); setEditClientForm({ name: client.name, wms_client_id: client.wms_client_id || '' }); }}
                            title="Edit client"
                          >
                            <Pencil size={14} />
                          </button>
                          <button 
                            className="action-delete"
                            onClick={(e) => { e.stopPropagation(); handleDeleteClient(client.id, client.name); }}
                            title="Delete client"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                        <ChevronRight size={18} className="chevron" />
                      </>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* BRANCHES LIST - When viewing a client or all branches */}
        {activeTab === 'branches' && (isSuperAdmin || isClientManager) && !selectedBranch && (
          <div className="admin-section">
            <div className="section-header">
              <h2>Estate Agency Branches</h2>
              <Button onClick={() => setShowBranchForm(true)} className="add-btn">
                <Plus size={16} /> Add Branch
              </Button>
            </div>

            {showBranchForm && (
              <form onSubmit={handleCreateBranch} className="admin-form">
                <h3>Create New Branch</h3>
                <div className="form-grid">
                  <div className="form-group">
                    <Label>Branch Name *</Label>
                    <Input 
                      value={branchForm.name}
                      onChange={(e) => setBranchForm({...branchForm, name: e.target.value})}
                      required
                      placeholder="e.g., London Office"
                    />
                  </div>
                  <div className="form-group">
                    <Label>Admin Name *</Label>
                    <Input 
                      value={branchForm.adminName}
                      onChange={(e) => setBranchForm({...branchForm, adminName: e.target.value})}
                      required
                      placeholder="Branch manager name"
                    />
                  </div>
                  <div className="form-group">
                    <Label>Admin Email *</Label>
                    <Input 
                      type="email"
                      value={branchForm.email}
                      onChange={(e) => setBranchForm({...branchForm, email: e.target.value})}
                      required
                      placeholder="admin@agency.com"
                    />
                  </div>
                  <div className="form-group">
                    <Label>Password *</Label>
                    <Input 
                      type="password"
                      value={branchForm.password}
                      onChange={(e) => setBranchForm({...branchForm, password: e.target.value})}
                      required
                      minLength={6}
                      placeholder="Min 6 characters"
                    />
                  </div>
                  <div className="form-group">
                    <Label>Address</Label>
                    <Input 
                      value={branchForm.address}
                      onChange={(e) => setBranchForm({...branchForm, address: e.target.value})}
                      placeholder="Branch address"
                    />
                  </div>
                  <div className="form-group">
                    <Label>Phone</Label>
                    <Input 
                      value={branchForm.phone}
                      onChange={(e) => setBranchForm({...branchForm, phone: e.target.value})}
                      placeholder="Contact number"
                    />
                  </div>
                  <div className="form-group full-width">
                    <Label>Agency Style</Label>
                    <select 
                      className="form-select"
                      value={branchForm.agencyStyle}
                      onChange={(e) => setBranchForm({...branchForm, agencyStyle: e.target.value})}
                    >
                      {agencyStyleOptions.map(style => (
                        <option key={style.value} value={style.value}>{style.value} — {style.description}</option>
                      ))}
                    </select>
                    <p className="form-helper">
                      {agencyStyleOptions.find(s => s.value === branchForm.agencyStyle)?.description}
                    </p>
                  </div>
                </div>
                <div className="form-actions">
                  <Button type="button" variant="outline" onClick={() => setShowBranchForm(false)}>Cancel</Button>
                  <Button type="submit">Create Branch</Button>
                </div>
              </form>
            )}

            <div className="items-list">
              {filteredBranches.length === 0 ? (
                <div className="empty-state">
                  <Building2 size={48} />
                  <p>{dashSearch ? 'No branches matching your search' : 'No branches yet'}</p>
                </div>
              ) : (
                filteredBranches.map((branch) => {
                  const branchAvatarCount = avatars.filter(a => a.branch_id === branch.id).length;
                  return (
                    <div 
                      key={branch.id} 
                      className={`item-card ${branch.enabled ? 'clickable' : 'branch-disabled'}`}
                      onClick={() => branch.enabled && handleSelectBranch(branch)}
                      data-testid={`branch-card-${branch.id}`}
                    >
                      <div className="item-icon branch-icon">
                        <Building2 size={20} />
                      </div>
                      <div className="item-info">
                        <span className="item-name">
                          {branch.name}
                          {branch.enabled && branch.warming_up && <span className="warming-badge">Setting up...</span>}
                        </span>
                        {branch.client_name && !selectedClient && (
                          <span className="item-client">
                            <Briefcase size={10} /> {branch.client_name}
                          </span>
                        )}
                        {branch.admin_name && (
                          <span className="item-meta">
                            Admin: {branch.admin_name} ({branch.admin_email})
                          </span>
                        )}
                        <span className="item-stats">
                          {branch.agent_count} agents • {branchAvatarCount} avatars
                        </span>
                      </div>
                      <div className="item-actions" onClick={(e) => e.stopPropagation()}>
                        {togglingBranchId === branch.id ? (
                          <span className="toggle-loading">
                            <Loader2 size={14} className="spin" />
                            {!branch.enabled ? 'Importing agents...' : 'Disabling...'}
                          </span>
                        ) : (
                          <button
                            className={`toggle-btn ${branch.enabled ? 'enabled' : 'disabled'}`}
                            onClick={() => handleToggleBranch(branch.id, branch.name, branch.enabled)}
                            title={branch.enabled ? 'Disable branch' : 'Enable branch & import agents'}
                            data-testid={`toggle-branch-${branch.id}`}
                          >
                            {branch.enabled ? 'Enabled' : 'Disabled'}
                          </button>
                        )}
                      </div>
                      <ChevronRight size={18} className="chevron" />
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}

        {/* EDITORS MANAGEMENT - Super Admin Only */}
        {activeTab === 'editors' && isSuperAdmin && !selectedBranch && (
          <div className="admin-section">
            <div className="section-header">
              <h2>Editor Accounts</h2>
              <Button onClick={() => setShowEditorForm(true)} className="add-btn">
                <Plus size={16} /> Add Editor
              </Button>
            </div>

            {showEditorForm && (
              <form onSubmit={handleCreateEditor} className="admin-form">
                <h3>Create New Editor</h3>
                <div className="form-grid">
                  <div className="form-group">
                    <Label>Name *</Label>
                    <Input 
                      value={editorForm.name}
                      onChange={(e) => setEditorForm({...editorForm, name: e.target.value})}
                      required
                      placeholder="Editor name"
                    />
                  </div>
                  <div className="form-group">
                    <Label>Email *</Label>
                    <Input 
                      type="email"
                      value={editorForm.email}
                      onChange={(e) => setEditorForm({...editorForm, email: e.target.value})}
                      required
                      placeholder="editor@agency.com"
                    />
                  </div>
                  <div className="form-group">
                    <Label>Password *</Label>
                    <Input 
                      type="password"
                      value={editorForm.password}
                      onChange={(e) => setEditorForm({...editorForm, password: e.target.value})}
                      required
                      minLength={6}
                      placeholder="Min 6 characters"
                    />
                  </div>
                </div>
                <div className="form-actions">
                  <Button type="button" variant="outline" onClick={() => setShowEditorForm(false)}>Cancel</Button>
                  <Button type="submit">Create Editor</Button>
                </div>
              </form>
            )}

            <div className="items-list">
              {filteredEditors.length === 0 ? (
                <div className="empty-state">
                  <FileText size={48} />
                  <p>{dashSearch ? 'No editors matching your search' : 'No editors yet'}</p>
                </div>
              ) : (
                filteredEditors.map((editor) => (
                  <div key={editor.id} className="item-card" data-testid={`editor-card-${editor.id}`}>
                    <div className="item-icon editor-icon">
                      <FileText size={20} />
                    </div>
                    <div className="item-info">
                      <span className="item-name">{editor.name}</span>
                      <span className="item-meta">
                        <Mail size={12} /> {editor.email}
                      </span>
                      <span className="item-stats">
                        Added {formatDate(editor.created_at)}
                      </span>
                    </div>
                    <div className="item-actions">
                      <button 
                        className="delete-btn"
                        onClick={() => handleDeleteEditor(editor.id)}
                        title="Remove editor"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* USERS & ROLES - Super Admin Only */}
        {activeTab === 'users' && isSuperAdmin && !selectedBranch && (
          <div className="admin-section" data-testid="users-roles-section">
            <div className="users-view-switch" role="tablist" data-testid="users-view-switch">
              <button role="tab" aria-selected={usersView === 'users'} className={`users-view-btn ${usersView === 'users' ? 'active' : ''}`} onClick={() => setUsersView('users')} data-testid="users-view-users"><Users size={13} /> Users</button>
              <button role="tab" aria-selected={usersView === 'access'} className={`users-view-btn ${usersView === 'access' ? 'active' : ''}`} onClick={() => setUsersView('access')} data-testid="users-view-access"><Shield size={13} /> Roles & Access</button>
            </div>
            {usersView === 'access' && <RolesAccess />}
            {accessUser && <UserAccessModal user={accessUser} onClose={() => { setAccessUser(null); loadUsersData(usersSearch, roleFilter, branchFilter); }} />}
            {usersView === 'users' && (<>
            {/* Role summary pills */}
            <div className="role-pills-row">
              <button className={`role-pill ${!roleFilter ? 'active' : ''}`} onClick={() => handleRoleFilter('')} data-testid="filter-all">
                All ({allUsers.length})
              </button>
              {roleSummary.map(r => {
                const RoleIcon = ROLE_ICONS[r.id] || User;
                return (
                  <button key={r.id} className={`role-pill ${roleFilter === r.id ? 'active' : ''}`} onClick={() => handleRoleFilter(r.id)} data-testid={`filter-${r.id}`}>
                    <RoleIcon size={12} /> {r.label} ({r.user_count})
                  </button>
                );
              })}
            </div>

            {/* Filters + Create */}
            <div className="users-toolbar">
              <select
                className="users-branch-filter"
                value={branchFilter}
                onChange={(e) => handleBranchFilterChange(e.target.value)}
                data-testid="users-branch-filter"
              >
                <option value="">All Branches</option>
                {branches.map(b => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
              <button className="create-user-btn" onClick={() => setShowCreateUser(true)} data-testid="create-user-btn">
                <Plus size={14} /> New User
              </button>
            </div>

            {/* Create User Form */}
            {showCreateUser && (
              <div className="user-form-card" data-testid="create-user-form">
                <h3>Create User</h3>
                <div className="user-form-grid">
                  <input placeholder="Full Name" value={createUserForm.name} onChange={(e) => setCreateUserForm(f => ({...f, name: e.target.value}))} data-testid="create-name" />
                  <input placeholder="Email" value={createUserForm.email} onChange={(e) => setCreateUserForm(f => ({...f, email: e.target.value}))} data-testid="create-email" />
                  <input placeholder="Password" type="password" value={createUserForm.password} onChange={(e) => setCreateUserForm(f => ({...f, password: e.target.value}))} data-testid="create-password" />
                  <select value={createUserForm.role} onChange={(e) => setCreateUserForm(f => ({...f, role: e.target.value}))} data-testid="create-role">
                    {roleSummary.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
                  </select>
                  {createUserForm.role === 'client_manager' ? (
                    <select value={createUserForm.client_id} onChange={(e) => setCreateUserForm(f => ({...f, client_id: e.target.value}))} data-testid="create-client">
                      <option value="">No Client</option>
                      {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  ) : createUserForm.role === 'social_manager' ? (
                    <div style={{ maxHeight: 130, overflowY: 'auto', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 8, padding: '6px 10px', display: 'flex', flexDirection: 'column', gap: 4 }} data-testid="create-branch-multi">
                      <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600 }}>Branches ({createUserForm.branch_ids.length} selected)</span>
                      {branches.map(b => (
                        <label key={b.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={createUserForm.branch_ids.includes(b.id)}
                            onChange={(e) => setCreateUserForm(f => ({ ...f, branch_ids: e.target.checked ? [...f.branch_ids, b.id] : f.branch_ids.filter(x => x !== b.id) }))}
                          />
                          {b.name}
                        </label>
                      ))}
                    </div>
                  ) : (
                    <select value={createUserForm.branch_id} onChange={(e) => setCreateUserForm(f => ({...f, branch_id: e.target.value}))} data-testid="create-branch">
                      <option value="">No Branch</option>
                      {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                    </select>
                  )}
                  <input placeholder="Phone (optional)" value={createUserForm.phone} onChange={(e) => setCreateUserForm(f => ({...f, phone: e.target.value}))} data-testid="create-phone" />
                </div>
                <div className="user-form-actions">
                  <button className="form-save-btn" onClick={handleCreateUser} data-testid="create-submit">Create</button>
                  <button className="form-cancel-btn" onClick={() => setShowCreateUser(false)}>Cancel</button>
                </div>
              </div>
            )}

            {/* User List */}
            {usersLoading ? (
              <div className="admin-loading" style={{ padding: '40px 0' }}>
                <Loader2 className="animate-spin" size={24} />
                <span>Loading users...</span>
              </div>
            ) : (
              <div className="users-table" data-testid="users-table">
                <div className="users-table-header">
                  <span className="col-name">Name</span>
                  <span className="col-email">Email</span>
                  <span className="col-role">Role</span>
                  <span className="col-branch">Branch / Client</span>
                  <span className="col-password">Password</span>
                  <span className="col-actions">Actions</span>
                </div>
                {allUsers.length === 0 ? (
                  <div className="users-empty">No users found</div>
                ) : (
                  allUsers.map(u => (
                    <div key={u.id} className="user-table-row" data-testid={`user-row-${u.id}`}>
                      {editingUser?.id === u.id ? (
                        /* Inline edit mode */
                        <>
                          <span className="col-name">
                            <input value={editForm.name} onChange={(e) => setEditForm(f => ({...f, name: e.target.value}))} className="inline-edit" data-testid="edit-name" />
                          </span>
                          <span className="col-email">
                            <input value={editForm.email} onChange={(e) => setEditForm(f => ({...f, email: e.target.value}))} className="inline-edit" data-testid="edit-email" />
                          </span>
                          <span className="col-role">
                            <select value={editForm.role} onChange={(e) => setEditForm(f => ({...f, role: e.target.value}))} className="inline-select" data-testid="edit-role">
                              {editForm.role && !roleSummary.find(r => r.id === editForm.role) && (
                                <option value={editForm.role}>{editForm.role.replace(/_/g, ' ')}</option>
                              )}
                              {roleSummary.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
                            </select>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, marginTop: 5, cursor: 'pointer', color: '#f59e0b', fontWeight: 600 }} data-testid="edit-demo-user">
                              <input type="checkbox" checked={!!editForm.demo_user} onChange={(e) => setEditForm(f => ({...f, demo_user: e.target.checked}))} />
                              Demo User (unlocks Social Video)
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, marginTop: 4, cursor: 'pointer', color: '#96C11F', fontWeight: 600 }} data-testid="edit-asset-hub-access">
                              <input type="checkbox" checked={!!editForm.asset_hub_access} onChange={(e) => setEditForm(f => ({...f, asset_hub_access: e.target.checked}))} />
                              Asset Hub (Orders view in Social Library)
                            </label>
                          </span>
                          <span className="col-branch">
                            {editForm.role === 'client_manager' ? (
                              <select value={editForm.client_id || ''} onChange={(e) => setEditForm(f => ({...f, client_id: e.target.value}))} className="inline-select" data-testid="edit-client">
                                <option value="">No Client</option>
                                {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                              </select>
                            ) : editForm.role === 'social_manager' ? (
                              <div style={{ maxHeight: 110, overflowY: 'auto', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 8, padding: '4px 8px', display: 'flex', flexDirection: 'column', gap: 3 }} data-testid="edit-branch-multi">
                                {branches.map(b => (
                                  <label key={b.id} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, cursor: 'pointer' }}>
                                    <input
                                      type="checkbox"
                                      checked={(editForm.branch_ids || []).includes(b.id)}
                                      onChange={(e) => setEditForm(f => ({ ...f, branch_ids: e.target.checked ? [...(f.branch_ids || []), b.id] : (f.branch_ids || []).filter(x => x !== b.id) }))}
                                    />
                                    {b.name}
                                  </label>
                                ))}
                              </div>
                            ) : (
                              <select value={editForm.branch_id} onChange={(e) => setEditForm(f => ({...f, branch_id: e.target.value}))} className="inline-select" data-testid="edit-branch">
                                <option value="">None</option>
                                {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                              </select>
                            )}
                          </span>
                          <span className="col-password">—</span>
                          <span className="col-actions">
                            <button className="action-save" onClick={handleSaveUser} data-testid="save-user-btn"><Check size={14} /></button>
                            <button className="action-cancel" onClick={() => setEditingUser(null)}><X size={14} /></button>
                          </span>
                        </>
                      ) : (
                        /* Display mode */
                        <>
                          <span className="col-name">
                            {u.name}
                            {u.demo_user ? <span style={{ marginLeft: 6, fontSize: 9, fontWeight: 800, letterSpacing: '0.05em', background: '#f59e0b', color: '#1a1a1a', borderRadius: 999, padding: '2px 7px', verticalAlign: 'middle' }} data-testid={`demo-tag-${u.id}`}>DEMO</span> : null}
                            {u.tool_overrides && Object.keys(u.tool_overrides).length ? <span style={{ marginLeft: 6, fontSize: 9, fontWeight: 800, letterSpacing: '0.05em', background: 'rgba(150,193,31,0.25)', color: '#d6ec96', border: '1px solid rgba(150,193,31,0.5)', borderRadius: 999, padding: '1px 7px', verticalAlign: 'middle' }} title="Has per-account access exceptions" data-testid={`access-tag-${u.id}`}>{Object.keys(u.tool_overrides).length} ACCESS</span> : null}
                          </span>
                          <span className="col-email">{u.email}</span>
                          <span className="col-role">
                            <span className={`role-badge-sm role-color-${u.role}`}>{u.role.replace(/_/g, ' ')}</span>
                          </span>
                          <span className="col-branch">{u.role === 'social_manager' && u.branch_names?.length ? u.branch_names.join(', ') : (u.client_name || u.branch_name || '—')}</span>
                          <span className="col-password">
                            {u.password_plain ? (
                              <span className="pw-cell" data-testid={`pw-cell-${u.id}`}>
                                <span className="pw-mask">••••••••</span>
                                <button
                                  className="action-copy"
                                  title="Copy password"
                                  data-testid={`pw-copy-${u.id}`}
                                  onClick={() => { navigator.clipboard.writeText(u.password_plain); toast.success(`Password for ${u.email} copied`); }}
                                >
                                  <Copy size={13} />
                                </button>
                              </span>
                            ) : '—'}
                          </span>
                          <span className="col-actions">
                            <button className="action-edit" onClick={() => setAccessUser(u)} title="Per-account tool access" data-testid={`access-btn-${u.id}`}><Shield size={14} /></button>
                            <button className="action-edit" onClick={() => handleEditUser(u)} data-testid={`edit-btn-${u.id}`}><Pencil size={14} /></button>
                            <button className="action-login" onClick={() => handleLoginAsUser(u.id)} title={`Login as ${u.name}`}><LogIn size={14} /></button>
                            <button className="action-delete" onClick={() => handleDeleteUser(u.id, u.name)} data-testid={`delete-btn-${u.id}`}><Trash2 size={14} /></button>
                          </span>
                        </>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}
            </>)}
          </div>
        )}

        {/* VIDEO LIBRARY - Super Admin */}
        {activeTab === 'videos' && isSuperAdmin && (
          <VideoLibrary />
        )}

        {/* BRANCH SETTINGS - For Branch Admin */}
        {isBranchAdmin && myBranch && (
          <div className="admin-section branch-settings-section">
            <div className="section-header">
              <h2>Branch Settings</h2>
            </div>
            <div className="branch-settings-card">
              <div className="setting-row">
                <div className="setting-info">
                  <span className="setting-label">Agency Style</span>
                  <span className="setting-description">The tone and style applied to all agent scripts in this branch</span>
                </div>
                <select 
                  className="style-select enhanced"
                  value={myBranch.agency_style || 'Friendly & Conversational'}
                  onChange={(e) => handleUpdateBranchStyle(myBranch.id, e.target.value)}
                >
                  {agencyStyleOptions.map(style => (
                    <option key={style.value} value={style.value}>{style.value} — {style.description}</option>
                  ))}
                </select>
                <p className="style-helper">
                  {agencyStyleOptions.find(s => s.value === (myBranch.agency_style || 'Friendly & Conversational'))?.description}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* TEMPLATE EDITOR - When "Template Editor" clicked for a branch */}
        {showBranchTemplates && isSuperAdmin && selectedBranch && (
          <TemplateEditor branch={selectedBranch} onClose={() => setShowBranchTemplates(false)} />
        )}

        {/* CAPTION STYLE EDITOR */}
        {showCaptionStyle && isSuperAdmin && selectedBranch && (
          <CaptionStyleEditor
            branchId={selectedBranch.id}
            branchName={selectedBranch.name}
            onClose={() => setShowCaptionStyle(false)}
          />
        )}

        {/* BRAND STYLER (inheritance) — branch */}
        {showBrandStyle && isSuperAdmin && selectedBranch && (
          <BrandStylePanel
            scope="branch"
            target={selectedBranch}
            onClose={() => setShowBrandStyle(false)}
            onOpenStyler={() => { setShowBrandStyle(false); setSelectedBranch(null); setSelectedClient(null); handleTabChange('brand_styler'); window.scrollTo(0, 0); }}
          />
        )}

        {/* BRAND PERSONALITY EDITOR */}
        {showBrandPersonality && isSuperAdmin && selectedBranch && (
          <BrandPersonalityEditor
            branch={selectedBranch}
            onSaved={(updated) => {
              setSelectedBranch(updated);
              setBranches(prev => prev.map(b => b.id === updated.id ? updated : b));
            }}
            onClose={() => setShowBrandPersonality(false)}
          />
        )}

        {/* VIDEO AUTOMATION — Synthesia link + Social Pro gating */}
        {showVideoAutomation && isSuperAdmin && selectedBranch && (
          <VideoAutomationModal
            branch={selectedBranch}
            onSaved={(syn) => {
              setSelectedBranch(prev => prev ? { ...prev, synthesia_linked: !!syn?.has_key } : prev);
            }}
            onClose={() => setShowVideoAutomation(false)}
          />
        )}

        {/* AVATARS VIEW - For Branch Admin or when Super Admin selects a branch */}
        {!showBranchTemplates && !showCaptionStyle && !showBrandPersonality && !showBrandStyle && (activeTab === 'avatars' || (isSuperAdmin && selectedBranch)) && (
          <div className="admin-section avatars-section">
            {/* Key Contact / Branch Manager - prominent card at top */}
            {(isSuperAdmin || isClientManager) && selectedBranch && (
              <div className="key-contact-card" data-testid="key-contact-card">
                <div className="key-contact-header">
                  <div className="key-contact-title">
                    <Shield size={18} />
                    <span>Key Contact</span>
                  </div>
                  {!showManagerEdit && (
                    <Button variant="outline" size="sm" onClick={openManagerEdit} className="edit-contact-btn" data-testid="edit-key-contact">
                      {selectedBranch.admin_name ? 'Change' : 'Assign'}
                    </Button>
                  )}
                </div>
                {showManagerEdit ? (
                  <form onSubmit={handleUpdateBranchManager} className="key-contact-edit">
                    <div className="user-search-box" data-testid="manager-search-box">
                      <Search size={14} />
                      <input
                        type="text"
                        placeholder="Search users by name or email..."
                        value={managerSearchQuery}
                        onChange={(e) => handleManagerSearch(e.target.value)}
                        autoFocus
                        data-testid="manager-search-input"
                      />
                    </div>
                    {managerSearchResults.length > 0 && (
                      <div className="user-search-results" data-testid="manager-search-results">
                        {managerSearchResults.map(u => (
                          <button
                            key={u.id}
                            type="button"
                            className="user-search-item"
                            onClick={() => handleAssignManager(u)}
                            data-testid={`assign-manager-${u.id}`}
                          >
                            <span className="search-user-name">{u.name}</span>
                            <span className="search-user-email">{u.email}</span>
                            <span className={`role-pill-sm role-${u.role}`}>{u.role.replace(/_/g, ' ')}</span>
                          </button>
                        ))}
                      </div>
                    )}
                    {managerForm.name && (
                      <div className="assigned-contact-preview">
                        <User size={16} />
                        <div>
                          <span className="preview-name">{managerForm.name}</span>
                          <span className="preview-email">{managerForm.email}</span>
                        </div>
                      </div>
                    )}
                    <div className="key-contact-actions">
                      <Button type="button" variant="outline" size="sm" onClick={() => { setShowManagerEdit(false); setManagerSearchResults([]); }}>
                        Cancel
                      </Button>
                      <Button type="submit" size="sm" disabled={!managerForm.name}>
                        Save
                      </Button>
                    </div>
                  </form>
                ) : (
                  <div className="key-contact-display">
                    {selectedBranch.admin_name ? (
                      <>
                        <div className="contact-avatar">
                          {selectedBranch.admin_name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                        </div>
                        <div className="contact-details">
                          <span className="contact-name">{selectedBranch.admin_name}</span>
                          <span className="contact-email">
                            <Mail size={13} />
                            {selectedBranch.admin_email}
                          </span>
                        </div>
                      </>
                    ) : (
                      <span className="no-contact">No key contact assigned</span>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Branch Users Section */}
            {isSuperAdmin && selectedBranch && (
              <div className="branch-users-section" data-testid="branch-users-section">
                <div className="section-header">
                  <h2><Users size={16} /> Branch Team</h2>
                </div>
                {(() => {
                  const branchUsers = allUsers.filter(u => u.branch_id === selectedBranch.id);
                  if (branchUsers.length === 0) {
                    return <p className="no-branch-users">No users assigned to this branch</p>;
                  }
                  return (
                    <div className="branch-users-list">
                      {branchUsers.map(u => (
                        <div key={u.id} className="branch-user-row" data-testid={`branch-user-${u.id}`}>
                          <div className="branch-user-info">
                            <span className="branch-user-name">{u.name}</span>
                            <span className="branch-user-email">{u.email}</span>
                          </div>
                          <span className={`role-pill role-${u.role}`}>{u.role.replace(/_/g, ' ')}</span>
                          <button
                            className="login-as-btn"
                            onClick={() => handleLoginAsUser(u.id)}
                            data-testid={`login-as-${u.id}`}
                            title={`View as ${u.name}`}
                          >
                            <LogIn size={13} /> Login as User
                          </button>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Two-column layout for avatars */}
            <div className="avatars-main-content">
              <div className="avatars-list-panel">
                <div className="section-header">
                  <h2>Avatars {currentBranchName && `- ${currentBranchName}`}</h2>
                  {isSuperAdmin && (
                    <Button onClick={() => setShowAvatarForm(true)} className="add-btn">
                      <Plus size={16} /> Add Avatar
                    </Button>
                  )}
              </div>

              {/* Add Avatar Form - Super Admin only */}
              {showAvatarForm && isSuperAdmin && (
                <form onSubmit={handleCreateAvatar} className="admin-form">
                  <h3>Create New Avatar</h3>
                  <p className="form-help">Add an avatar slot for this branch.</p>
                  <div className="form-grid">
                    <div className="form-group">
                      <Label>Agent Name *</Label>
                      <Input 
                        value={avatarForm.agentName}
                        onChange={(e) => setAvatarForm({...avatarForm, agentName: e.target.value})}
                        required
                        placeholder="e.g., Emma Watson"
                      />
                    </div>
                    <div className="form-group">
                      <Label>Avatar ID *</Label>
                      <Input 
                        value={avatarForm.avatarId}
                        onChange={(e) => setAvatarForm({...avatarForm, avatarId: e.target.value})}
                        required
                        placeholder="e.g., anna_costume1_cameraA"
                      />
                    </div>
                    <div className="form-group">
                      <Label>Agent Personality</Label>
                      <select 
                        className="form-select"
                        value={avatarForm.agentPersonality}
                        onChange={(e) => setAvatarForm({...avatarForm, agentPersonality: e.target.value})}
                      >
                        {personalityOptions.map(p => (
                          <option key={p.value} value={p.value}>{p.value} — {p.description}</option>
                        ))}
                      </select>
                      <p className="form-helper">
                        {personalityOptions.find(p => p.value === avatarForm.agentPersonality)?.description}
                      </p>
                    </div>
                  </div>
                  <div className="form-actions">
                    <Button type="button" variant="outline" onClick={() => setShowAvatarForm(false)}>Cancel</Button>
                    <Button type="submit">Create Avatar</Button>
                  </div>
                </form>
              )}

              {/* Avatars List */}
              <div className="items-list">
                {filteredAvatars.length === 0 ? (
                  <div className="empty-state">
                    <Video size={48} />
                    <p>No avatars yet</p>
                    <span>{isSuperAdmin ? 'Add avatars for this branch' : 'No avatar slots have been set up'}</span>
                  </div>
                ) : (
                  filteredAvatars.map((avatar) => {
                    const assigned = isAvatarAssigned(avatar.id);
                    const assignedAgent = getAgentForAvatar(avatar.id);
                    
                    return (
                      <div 
                        key={avatar.id} 
                        className={`item-card clickable ${!assigned ? 'avatar-available' : ''} ${selectedAvatar?.id === avatar.id ? 'selected' : ''}`}
                        onClick={() => handleSelectAvatar(avatar)}
                      >
                        <div className={`item-icon avatar-icon-box ${!assigned ? 'available' : ''}`}>
                          <Video size={20} />
                        </div>
                        <div className="item-info">
                          <span className="item-name">{avatar.agent_name}</span>
                          <span className="item-meta">ID: {avatar.avatar_id}</span>
                          <span className="item-personality">
                            Personality: {avatar.agent_personality || 'Easy-going & Confident'}
                          </span>
                          <span className="item-date">
                            <Calendar size={10} /> Added {formatDate(avatar.created_at)}
                          </span>
                          {assigned ? (
                            <span className="avatar-status assigned">
                              <User size={10} /> {assignedAgent?.name} ({assignedAgent?.email})
                            </span>
                          ) : (
                            <span className="avatar-status available">Available</span>
                          )}
                        </div>
                        {/* Personality selector for admin */}
                        <div className="item-actions" onClick={(e) => e.stopPropagation()}>
                          <select 
                            className="personality-select"
                            value={avatar.agent_personality || 'Easy-going & Confident'}
                            onChange={(e) => handleUpdateAvatarPersonality(avatar.id, e.target.value)}
                            title={personalityOptions.find(p => p.value === (avatar.agent_personality || 'Easy-going & Confident'))?.description}
                          >
                            {personalityOptions.map(p => (
                              <option key={p.value} value={p.value}>{p.value}</option>
                            ))}
                          </select>
                        </div>
                        <ChevronRight size={18} className="chevron" />
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Avatar Details Panel */}
            {selectedAvatar ? (
              <div className="avatar-details-panel">
                <div className="avatar-detail-header">
                  <div className="avatar-detail-icon">
                    <Video size={32} />
                  </div>
                  <div className="avatar-detail-info">
                    <h3>{selectedAvatar.agent_name}</h3>
                    <span className="avatar-detail-id">ID: {selectedAvatar.avatar_id}</span>
                    <span className="avatar-detail-personality">
                      Personality: {selectedAvatar.agent_personality || 'Easy-going & Confident'}
                    </span>
                    <p className="personality-helper">
                      {personalityOptions.find(p => p.value === (selectedAvatar.agent_personality || 'Easy-going & Confident'))?.description}
                    </p>
                    <span className="avatar-detail-date">
                      <Calendar size={12} /> Added {formatDate(selectedAvatar.created_at)}
                    </span>
                  </div>
                  {isSuperAdmin && (
                    <button 
                      className="delete-btn"
                      onClick={() => handleDeleteAvatar(selectedAvatar.id)}
                      title="Delete avatar"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>

                {/* Assigned Agent Info */}
                {selectedAvatar.agent ? (
                  <div className="assigned-agent-section">
                    <h4>Assigned To</h4>
                    <div className="assigned-agent-card">
                      <div className="agent-avatar-icon">
                        <User size={20} />
                      </div>
                      <div className="agent-info">
                        <span className="agent-name">{selectedAvatar.agent.name}</span>
                        <span className="agent-email">
                          <Mail size={12} /> {selectedAvatar.agent.email}
                        </span>
                      </div>
                      <button 
                        className="remove-agent-btn"
                        onClick={() => handleDeleteAgent(selectedAvatar.agent.id)}
                        title="Remove agent"
                      >
                        <Trash2 size={14} />
                        Remove
                      </button>
                    </div>

                    {/* Scripts Section */}
                    <div className="scripts-section">
                      <h4><FileText size={16} /> Scripts ({avatarScripts.length})</h4>
                      {loadingScripts ? (
                        <div className="loading-scripts">
                          <Loader2 className="animate-spin" size={20} />
                        </div>
                      ) : avatarScripts.length === 0 ? (
                        <p className="no-scripts">No scripts yet</p>
                      ) : (
                        <div className="scripts-list">
                          {avatarScripts.map((script) => (
                            <div key={script.id} className="script-item">
                              <FileText size={16} />
                              <div className="script-info">
                                <span className="script-title">{script.title}</span>
                                <span className="script-meta">
                                  <Clock size={12} /> {formatDate(script.created_at)} • {script.section_count} sections • {formatDuration(script.total_duration)}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="unassigned-section">
                    <div className="unassigned-message">
                      <Phone size={32} />
                      <p>This avatar slot is available</p>
                      <span>To assign an agent to this slot, please call your NicheCom representative</span>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="avatar-details-panel avatar-details-empty">
                <div className="empty-details-state">
                  <Video size={40} />
                  <p>Select an avatar</p>
                  <span>Click on an avatar to view details</span>
                </div>
              </div>
            )}
            </div> {/* Close avatars-main-content */}
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminPanel;
